import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useCallback } from "react";
import { useQueryCache } from "./useQueryCache";
import { Database } from "@/utils/supabase.types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

type Collection = Database["reactcomponents"]["Tables"]["collections"]["Row"];

export function uniqueItem<T>(v: T, i: number, a: T[]): boolean {
  return a.indexOf(v) === i;
}

export function formatAuthorId(authorId?: string, decode = false): string {
  if (!authorId) return "";

  if (decode) {
    return authorId.replace("-", "|");
  }

  return authorId.replace("|", "-");
}

interface FetchComponentsParams {
  sortBy?: string;
  selectedCollections?: string[];
  currentTags?: string[];
  range?: [number, number];
  searchQuery?: string;
  select?: string;
  count?: boolean;
  isPublic?: boolean;
  authorId?: number;
  deleted?: boolean;
  getTotalCount?: boolean;
}

interface ComponentCounts {
  collections: Record<string, number>;
  tags: Record<string, number>; // General tags (non-layout, non-platform)
}

// Define special tag categories
const platformTagNames = ["web", "mobile"];
const layoutTagNames = ["stacked", "sidebar", "multi-column"];

// Helper function to find intersection of multiple ID arrays
const intersectIds = (
  ...idArrays: (number[] | null | undefined)[]
): number[] => {
  const validArrays = idArrays.filter(
    (arr): arr is number[] => arr !== null && arr !== undefined
  );
  if (validArrays.length === 0) return [];
  if (validArrays.length === 1) return [...new Set(validArrays[0])]; // Return unique IDs if only one array

  let intersectionSet = new Set(validArrays[0]);

  for (let i = 1; i < validArrays.length; i++) {
    const currentSet = new Set(validArrays[i]);
    intersectionSet = new Set(
      [...intersectionSet].filter((id) => currentSet.has(id))
    );
    if (intersectionSet.size === 0) break;
  }

  return Array.from(intersectionSet);
};

export function useComponentsQuery() {
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const { executeQuery, invalidateCache } = useQueryCache();
  const pendingApprovalEnabled = useFeatureFlagEnabled(
    "pending_approval_reactcomponents"
  );

  // Helper to get component IDs based on basic filters + search
  const getBaseFilteredComponentIds = useCallback(
    async (searchQuery?: string, isPublic = true, deleted = false) => {
      if (!supabaseClient) return null;

      let query = supabaseClient
        .schema("reactcomponents")
        .from("components")
        .select("id");

      if (isPublic !== undefined && !pendingApprovalEnabled) {
        query = query.eq("public", isPublic);
      }
      if (deleted !== undefined) {
        query = query.eq("deleted", deleted);
      }
      if (searchQuery) {
        query = query.or(
          `name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`
        );
      }

      const queryKey = `base-component-ids-${JSON.stringify({
        searchQuery,
        isPublic,
        deleted,
      })}`;
      const { data } = await executeQuery<{ id: number }[]>(query, queryKey);
      return data?.map((c) => c.id) || [];
    },
    [supabaseClient, executeQuery]
  );

  // Helper to get component IDs for specific collections
  const getComponentIdsForCollections = useCallback(
    async (collectionIds: string[]) => {
      if (!supabaseClient || collectionIds.length === 0) return [];
      const query = supabaseClient
        .schema("reactcomponents")
        .from("components_collections")
        .select("component_id")
        .in("collection_id", collectionIds.map(Number));

      const queryKey = `collections-ids-${collectionIds.join(",")}`;
      const { data } = await executeQuery<{ component_id: number }[]>(
        query,
        queryKey
      );
      return data?.map((item) => item.component_id).filter(uniqueItem) || [];
    },
    [supabaseClient, executeQuery]
  );

  // Helper to get component IDs matching ANY of the given tags
  const getComponentIdsForTags = useCallback(
    async (tagNames: string[]) => {
      const uniqueValidTagNames = tagNames.filter(
        (tag, index, self) => tag && self.indexOf(tag) === index
      );
      if (!supabaseClient || uniqueValidTagNames.length === 0) return [];

      const query = supabaseClient
        .schema("reactcomponents")
        .from("components_tags")
        .select("component_id, tags!inner(id, name)")
        .in("tags.name", uniqueValidTagNames);

      const queryKey = `tags-ids-${uniqueValidTagNames.sort().join(",")}`;
      const { data } = await executeQuery<{ component_id: number }[]>(
        query,
        queryKey
      );
      return data?.map((item) => item.component_id).filter(uniqueItem) || [];
    },
    [supabaseClient, executeQuery]
  );

  const getFinalFilteredComponentIds = useCallback(
    async (
      searchQuery?: string,
      isPublic = true,
      deleted = false,
      selectedCollections: string[] = [],
      currentTags: string[] = []
    ): Promise<number[] | null> => {
      // 1. Base IDs
      const baseIds = await getBaseFilteredComponentIds(
        searchQuery,
        isPublic,
        deleted
      );
      if (baseIds === null) return null; // Error fetching base IDs
      if (baseIds.length === 0) return []; // Early exit

      // 2. Collection IDs
      const collectionIds =
        selectedCollections.length > 0
          ? await getComponentIdsForCollections(selectedCollections)
          : null; // null indicates no collection filter applied
      if (
        selectedCollections.length > 0 &&
        (!collectionIds || collectionIds.length === 0)
      ) {
        return []; // Early exit if collections selected but none found
      }

      // 3. Separate Tags and Fetch IDs for each group
      const uniqueCurrentTags = currentTags.filter(
        (tag, index, self) => tag && self.indexOf(tag) === index
      );
      const currentLayoutTags = uniqueCurrentTags.filter((tag) =>
        layoutTagNames.includes(tag)
      );
      const currentPlatformTags = uniqueCurrentTags.filter((tag) =>
        platformTagNames.includes(tag)
      );
      const currentGeneralTags = uniqueCurrentTags.filter(
        (tag) =>
          !layoutTagNames.includes(tag) && !platformTagNames.includes(tag)
      );

      const layoutIds =
        currentLayoutTags.length > 0
          ? await getComponentIdsForTags(currentLayoutTags)
          : null;
      if (
        currentLayoutTags.length > 0 &&
        (!layoutIds || layoutIds.length === 0)
      )
        return [];

      const platformIds =
        currentPlatformTags.length > 0
          ? await getComponentIdsForTags(currentPlatformTags)
          : null;
      if (
        currentPlatformTags.length > 0 &&
        (!platformIds || platformIds.length === 0)
      )
        return [];

      const generalTagIds =
        currentGeneralTags.length > 0
          ? await getComponentIdsForTags(currentGeneralTags)
          : null;
      if (
        currentGeneralTags.length > 0 &&
        (!generalTagIds || generalTagIds.length === 0)
      )
        return [];

      // 4. Intersect all ID sets (base, collections, layout, platform, general)
      // The intersectIds helper handles nulls correctly (null = no filter for that dimension)
      const finalIds = intersectIds(
        baseIds,
        collectionIds,
        layoutIds,
        platformIds,
        generalTagIds
      );

      return finalIds;
    },
    [
      getBaseFilteredComponentIds,
      getComponentIdsForCollections,
      getComponentIdsForTags,
    ]
  );

  const getComponentCounts = useCallback(
    async ({
      searchQuery,
      isPublic = true,
      deleted = false,
      selectedCollections = [],
      currentTags = [], // These are the tags used by the *user interface* filters
    }: {
      searchQuery?: string;
      isPublic?: boolean;
      deleted?: boolean;
      selectedCollections?: string[];
      currentTags?: string[];
    } = {}): Promise<ComponentCounts> => {
      if (!supabaseClient) {
        return { collections: {}, tags: {} };
      }

      const counts: ComponentCounts = {
        collections: {},
        tags: {},
      };

      try {
        // --- Calculate Collection Counts ---
        // Find IDs matching base AND all relevant *tag* filters (layout, platform, general)
        // Collections filter is NOT applied here, as we count components *within* collections matching the tags/search
        const idsForCollectionCount = await getFinalFilteredComponentIds(
          searchQuery,
          isPublic,
          deleted,
          [],
          currentTags // No collection filter here
        );

        if (idsForCollectionCount === null)
          throw new Error("Failed to filter IDs for collection count");
        if (idsForCollectionCount.length === 0) return counts; // No components match filters

        // Fetch and aggregate collection counts for these IDs
        const collectionsCountQuery = supabaseClient
          .schema("reactcomponents")
          .from("components_collections")
          .select("collection_id")
          .in("component_id", idsForCollectionCount);

        const collectionsCountQueryKey = `collection-counts-for-final-${idsForCollectionCount
          .sort()
          .join(",")}`;
        const { data: collectionData } = await executeQuery<
          { collection_id: number }[]
        >(collectionsCountQuery, collectionsCountQueryKey);

        if (collectionData) {
          collectionData.forEach((item) => {
            const collectionId = item.collection_id.toString();
            counts.collections[collectionId] =
              (counts.collections[collectionId] || 0) + 1;
          });
        }

        // --- Calculate Tag/Layout Counts ---
        // Find IDs matching base AND collections AND *platform* tags
        // Layout/General tags are NOT applied here, as we are counting components *having* those tags after other filters
        const uniqueCurrentTags = currentTags.filter(
          (tag, index, self) => tag && self.indexOf(tag) === index
        );
        const platformCurrentTags = uniqueCurrentTags.filter((tag) =>
          platformTagNames.includes(tag)
        );

        const idsForTagCounting = await getFinalFilteredComponentIds(
          searchQuery,
          isPublic,
          deleted,
          selectedCollections,
          platformCurrentTags // Only apply platform tags here
        );

        if (idsForTagCounting === null)
          throw new Error("Failed to filter IDs for tag/layout count");
        if (idsForTagCounting.length === 0) return counts; // No components match base/collection/platform filters

        // Fetch all tags associated with these components
        const tagsQuery = supabaseClient
          .schema("reactcomponents")
          .from("components_tags")
          .select("component_id, tags!inner(name)")
          .in("component_id", idsForTagCounting);

        const tagsQueryKey = `tags-for-tag-counting-${idsForTagCounting
          .sort()
          .join(",")}`;
        const { data: componentTagData } = await executeQuery<
          { component_id: number; tags: { name: string } }[]
        >(tagsQuery, tagsQueryKey);

        if (componentTagData) {
          // Aggregate counts per tag type (layout or general), ensuring uniqueness per component
          const componentTagMap = new Map<number, Set<string>>();
          componentTagData.forEach(({ component_id, tags }) => {
            if (!componentTagMap.has(component_id)) {
              componentTagMap.set(component_id, new Set<string>());
            }
            componentTagMap.get(component_id)?.add(tags.name);
          });

          componentTagMap.forEach((tagsSet) => {
            tagsSet.forEach((tagName) => {
              if (!platformTagNames.includes(tagName)) {
                // Don't count platform tags themselves
                counts.tags[tagName] = (counts.tags[tagName] || 0) + 1;
              }
            });
          });
        }

        return counts;
      } catch (error: any) {
        console.error(
          "Error fetching component counts:",
          error.message || error
        );
        return { collections: {}, tags: {} }; // Return empty counts on error
      }
    },
    [supabaseClient, executeQuery, getFinalFilteredComponentIds] // Add new dependency
  );

  const fetchComponents = useCallback(
    async ({
      sortBy = "-created_at",
      selectedCollections = [],
      currentTags = [],
      range = [0, 19],
      searchQuery,
      select = "*",
      count,
      isPublic = true, // Default to true if undefined
      authorId,
      deleted = false, // Default to false if undefined
      getTotalCount = false,
    }: FetchComponentsParams = {}) => {
      if (!supabaseClient) {
        return { data: [], error: null, count: null };
      }

      // --- Get Final Component IDs using the centralized helper ---
      const finalComponentIds = await getFinalFilteredComponentIds(
        searchQuery,
        isPublic,
        deleted,
        selectedCollections,
        currentTags
      );

      if (finalComponentIds === null) {
        // Handle error fetching IDs
        return {
          data: [],
          error: { message: "Failed to determine component IDs" },
          count: 0,
        };
      }
      if (finalComponentIds.length === 0) {
        return { data: [], error: null, count: 0 }; // No components match filters
      }
      // --- End of ID Determination ---

      const baseFields = [
        "id",
        "name",
        "image_url",
        "description",
        "public",
        "dependencies",
        "created_at",
        "show_at_top",
        "authors(first_name, last_name)",
        "tags(name)",
      ];
      const finalSelect = select === "*" ? baseFields.join(", ") : select;

      let query = supabaseClient
        .schema("reactcomponents")
        .from("components")
        .select(
          finalSelect,
          count || getTotalCount ? { count: "exact" } : undefined
        )
        .in("id", finalComponentIds);

      if (authorId !== undefined) {
        query = query.eq("author_id", authorId);
      }

      // Apply Sorting
      const sortBys = sortBy.split(",");
      sortBys.forEach((sBy) => {
        const isDescending = sBy.startsWith("-");
        const sortKey = isDescending ? sBy.slice(1) : sBy;
        if (["created_at", "show_at_top", "id", "name"].includes(sortKey)) {
          query = query.order(sortKey as any, { ascending: !isDescending });
        } else {
          console.warn(
            `Attempted to sort by potentially invalid key: ${sortKey}`
          );
        }
      });
      if (!sortBy.includes("show_at_top")) {
        query = query.order("show_at_top", {
          ascending: false,
          nullsFirst: false,
        });
      }
      if (!sortBy.includes("created_at") && !sortBy.includes("-created_at")) {
        query = query.order("created_at", { ascending: false });
      }
      if (!sortBy.includes("id") && !sortBy.includes("-id")) {
        query = query.order("id", { ascending: true });
      }

      // Apply Range
      if (!getTotalCount && range) {
        query = query.range(range[0], range[1]);
      }

      const queryKey = `components-final-${JSON.stringify({
        finalIdsHash: finalComponentIds.length, // Use count or hash for brevity if needed finalComponentIds.sort().join(','),
        sortBy,
        range: getTotalCount ? "total" : range,
        select: finalSelect,
        count: count || getTotalCount,
        authorId,
      })}`;

      return executeQuery(query, queryKey);
    },
    [supabaseClient, executeQuery, getFinalFilteredComponentIds] // Add new dependency
  );

  const fetchComponentCount = useCallback(
    async ({
      isPublic,
      deleted,
    }: {
      isPublic?: boolean;
      deleted?: boolean;
    } = {}) => {
      if (!supabaseClient) {
        return { data: null, error: null, count: 0 };
      }
      let query = supabaseClient
        .schema("reactcomponents")
        .from("components")
        .select("*", { count: "exact", head: true }); // Use head: true for count only

      if (isPublic !== undefined) {
        query = query.eq("public", isPublic);
      }

      if (deleted !== undefined) {
        query = query.eq("deleted", deleted);
      }

      const queryKey = `component-count-${JSON.stringify({
        isPublic,
        deleted,
      })}`;
      const result = await executeQuery(query, queryKey);
      return result;
    },
    [supabaseClient, executeQuery]
  );

  const fetchCollections = useCallback(async () => {
    if (!supabaseClient) {
      return { data: [], error: null, count: null };
    }
    const query = supabaseClient
      .schema("reactcomponents")
      .from("collections")
      .select("id, name, image_url, author_id") // Select desired fields
      .eq("public", true); // Fetch only public collections

    const queryKey = `collections-public`;
    const result = await executeQuery<Collection[]>(query, queryKey);
    return {
      data: result.data || [],
      error: result.error,
      count: result.count,
    };
  }, [supabaseClient, executeQuery]);

  return {
    fetchComponents,
    getComponentCounts,
    fetchComponentCount,
    fetchCollections,
    invalidateCache,
  };
}
